/**
 * Payments Page - Updated with full-page receipt view
 * Record and track water bill payments
 */

import React, { useState, useEffect } from 'react';
import { PaymentList } from '@components/payments/payment_list';
import { PaymentForm } from '@components/payments/payment_form';
import { PaymentReceipt } from '@components/payments/payment_receipt';
import { PageLoading } from '@components/common/loading_spinner';
import { toast } from 'react-toastify';
import {
  Payment,
  CreatePaymentPayload,
  PaymentReceipt as PaymentReceiptType
} from '@/types/payment_types';
import { Invoice } from '@/types/invoice_types';
import {
  fetch_payments,
  create_payment,
  fetch_payment_receipt,
  download_payment_receipt_pdf
} from '@services/payment_service';
import { fetch_invoices } from '@services/invoice_service';

type PageView = 'list' | 'receipt';

export const PaymentsPage: React.FC = () => {
  // State management
  const [payments, set_payments] = useState<Payment[]>([]);
  const [invoices, set_invoices] = useState<Invoice[]>([]);
  const [is_loading, set_is_loading] = useState(true);
  const [is_form_open, set_is_form_open] = useState(false);
  const [is_submitting, set_is_submitting] = useState(false);
  const [current_view, set_current_view] = useState<PageView>('list');

  const [selected_receipt, set_selected_receipt] = useState<PaymentReceiptType | null>(null);
  const [receipt_loading, set_receipt_loading] = useState(false);

  // Load data on mount
  useEffect(() => {
    load_initial_data();
  }, []);

  const load_initial_data = async () => {
    try {
      set_is_loading(true);

      // Load payments and unpaid invoices in parallel
      const [payments_data, invoices_data] = await Promise.all([
        fetch_payments(),
        fetch_invoices()
      ]);

      set_payments(payments_data);

      console.log("loaded invoices", invoices_data);
      // Filter using amount_outstanding instead of balance_due
      set_invoices(invoices_data?.filter(inv => parseFloat((inv as any).amount_outstanding) > 0));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load payments data');
    } finally {
      set_is_loading(false);
    }
  };

  const handle_add_payment = () => {
    if (invoices?.length === 0) {
      toast.warning('No outstanding invoices found. All invoices are fully paid.');
      return;
    }
    set_is_form_open(true);
  };

  const handle_view_receipt = async (payment: Payment) => {
    try {
      // Validate payment object
      if (!payment || !payment.id) {
        console.error('Invalid payment object:', payment);
        toast.error('Invalid payment data');
        return;
      }

      set_receipt_loading(true);
      set_current_view('receipt');

      // Pass the entire payment object instead of just the ID
      const receipt_data = await fetch_payment_receipt(payment);
      set_selected_receipt(receipt_data);
    } catch (error) {
      console.error('Error loading receipt:', error);
      toast.error('Failed to load payment receipt');
      set_current_view('list');
    } finally {
      set_receipt_loading(false);
    }
  };

  const handle_back_to_list = () => {
    set_current_view('list');
    set_selected_receipt(null);
  };

  const handle_form_submit = async (data: CreatePaymentPayload) => {
    try {
      set_is_submitting(true);

      const new_payment = await create_payment(data);
      console.log('New payment created:', new_payment);
      
      toast.success('Payment recorded successfully');

      set_is_form_open(false);
      await load_initial_data();

      // Automatically open receipt for the new payment if it has an ID
      if (new_payment && new_payment.id) {
        await handle_view_receipt(new_payment);
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      set_is_submitting(false);
    }
  };

  const handle_download_receipt = async (receipt: PaymentReceiptType) => {
    try {
      toast.info('Generating PDF...');
      const pdf_blob = await download_payment_receipt_pdf(receipt.payment.id);

      // Create download link
      const url = window.URL.createObjectURL(pdf_blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Receipt-${receipt.payment.payment_number || receipt.payment.reference_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Receipt downloaded successfully');
    } catch (error) {
      console.error('Error downloading receipt:', error);
      toast.error('Failed to download receipt PDF');
    }
  };

  const handle_print = () => {
    window.print();
  };

  const handle_export = async () => {
    try {
      toast.info('Exporting payments...');

      const csv_data = payments.map(payment => ({
        'Payment #': payment.payment_number || payment.reference_number,
        'Client': payment.client_name,
        'Invoice #': payment.invoice_number,
        'Amount': payment.amount,
        'Method': payment.payment_method,
        'Date': new Date(payment.payment_date).toLocaleDateString(),
        'Status': payment.status,
        'Reference': payment.reference_number || '-'
      }));

      const headers = Object.keys(csv_data[0]);
      const csv_content = [
        headers.join(','),
        ...csv_data.map(row => headers.map(header => `"${row[header as keyof typeof row]}"`).join(','))
      ].join('\n');

      const blob = new Blob([csv_content], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Payments exported successfully');
    } catch (error) {
      console.error('Error exporting payments:', error);
      toast.error('Failed to export payments');
    }
  };

  if (is_loading) {
    return <PageLoading message="Loading payments..." />;
  }

  // Show receipt view
  if (current_view === 'receipt') {
    return (
      <PaymentReceipt
        receipt={selected_receipt}
        on_download={handle_download_receipt}
        on_print={handle_print}
        on_back={handle_back_to_list}
        is_loading={receipt_loading}
      />
    );
  }

  // Show list view
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-charcoal">Payments</h1>
          <p className="text-medium-gray mt-1">
            Record and track water bill payments from clients
          </p>
        </div>
      </div>

      {/* Payment List */}
      <PaymentList
        payments={payments}
        is_loading={is_loading}
        on_add_payment={handle_add_payment}
        on_view_receipt={handle_view_receipt}
        on_export={handle_export}
      />

      {/* Payment Form Modal */}
      <PaymentForm
        is_open={is_form_open}
        on_close={() => set_is_form_open(false)}
        on_submit={handle_form_submit}
        invoices={invoices}
        is_loading={is_submitting}
      />
    </div>
  );
};